import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { fetchScenarioProgress } from "@/api/client";
import { Dialog, DialogContent } from "@/components/ui/dialog";

type ScenarioGenerationPopupProps = {
  open: boolean;
  sessionId?: string | null;
  onClose: () => void;
  onComplete: () => void;
  onError?: (message: string) => void;
  hideStatusCard?: boolean;
};

const STAGE_COUNT = 4;

export function ScenarioGenerationPopup({
  open,
  sessionId,
  onClose,
  onComplete,
  onError,
  hideStatusCard = false,
}: ScenarioGenerationPopupProps) {
  const completedRef = useRef(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // Buffered step label: delays UI update so each step stays visible ≥1500ms
  const [visibleStepLabel, setVisibleStepLabel] = useState<string | null>(null);
  const stepTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastStepShownAt = useRef<number>(0);

  useEffect(() => {
    if (open) {
      completedRef.current = false;
      setErrorMessage(null);
      setVisibleStepLabel(null);
      lastStepShownAt.current = 0;
    }
    return () => {
      if (stepTimerRef.current) clearTimeout(stepTimerRef.current);
    };
  }, [open]);

  const progressQuery = useQuery({
    queryKey: ["scenario-progress", sessionId],
    queryFn: () => fetchScenarioProgress(sessionId!),
    enabled: open && Boolean(sessionId),
    refetchInterval: open ? 1500 : false,
  });

  const steps = progressQuery.data ?? [];

  const doneCount = useMemo(() => steps.filter((s) => s.status === "done").length, [steps]);
  const runningStep = useMemo(() => steps.find((s) => s.status === "running"), [steps]);
  const erroredStep = useMemo(() => steps.find((s) => s.status === "error"), [steps]);

  // Delay showing each new step label by at least 1500ms so the user can read it
  useEffect(() => {
    if (!open) return;
    const newLabel = runningStep?.label ?? null;
    if (stepTimerRef.current) clearTimeout(stepTimerRef.current);
    const now = Date.now();
    const elapsed = now - lastStepShownAt.current;
    const delay = lastStepShownAt.current > 0 ? Math.max(0, 1500 - elapsed) : 0;
    stepTimerRef.current = setTimeout(() => {
      setVisibleStepLabel(newLabel);
      lastStepShownAt.current = Date.now();
    }, delay);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [runningStep?.label, open]);

  const segmentsTotal = Math.max(steps.length, STAGE_COUNT);
  const activeIndex = erroredStep
    ? steps.indexOf(erroredStep)
    : runningStep
      ? steps.indexOf(runningStep)
      : doneCount;

  const headline = erroredStep
    ? "Une erreur est survenue"
    : visibleStepLabel ?? "Préparation des scénarios en cours";
  const subline = erroredStep
    ? erroredStep.message ?? "Génération interrompue."
    : runningStep?.message ?? "Cela prend généralement environ deux minutes.";

  useEffect(() => {
    if (!open) return;
    if (completedRef.current) return;
    if (erroredStep) {
      completedRef.current = true;
      const msg = erroredStep.message ?? "Erreur pendant la génération.";
      setErrorMessage(msg);
      onError?.(msg);
      return;
    }
    if (steps.length > 0 && steps.every((s) => s.status === "done")) {
      completedRef.current = true;
      onComplete();
    }
  }, [steps, erroredStep, open, onComplete, onError]);

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent hideCloseButton className="max-w-md border border-blue-100 bg-white p-6 shadow-xl rounded-2xl" onInteractOutside={(e) => e.preventDefault()}>
        <style dangerouslySetInnerHTML={{__html: `
          @keyframes soundwave-pulse {
            0%, 100% { transform: scaleY(0.3); }
            50% { transform: scaleY(1); }
          }
          .soundwave-bar {
            transform-origin: bottom;
            animation: soundwave-pulse 1.2s ease-in-out infinite;
          }
        `}} />
        <div className="flex flex-col items-center text-center">
          {/* Animated Audio Soundwave Visualizer */}
          <div className="flex items-end justify-center gap-1.5 h-12 mb-6">
            <span className="w-1.5 h-full bg-blue-500 rounded-full soundwave-bar" style={{ animationDelay: '0.1s' }} />
            <span className="w-1.5 h-full bg-blue-400 rounded-full soundwave-bar" style={{ animationDelay: '0.3s' }} />
            <span className="w-1.5 h-full bg-blue-600 rounded-full soundwave-bar" style={{ animationDelay: '0.5s' }} />
            <span className="w-1.5 h-full bg-blue-500 rounded-full soundwave-bar" style={{ animationDelay: '0.2s' }} />
            <span className="w-1.5 h-full bg-blue-400 rounded-full soundwave-bar" style={{ animationDelay: '0.4s' }} />
          </div>

          <h3 className="text-xl font-bold text-[#0F172B] tracking-tight">
            {headline}
          </h3>
          
          <p className="text-[#64748B] text-sm font-normal mt-2 max-w-[320px]">
            {subline}
          </p>

          {/* Status Card */}
          {!hideStatusCard && (
            <div className="w-full mt-6 px-4 py-3.5 bg-blue-50/50 border border-blue-100/50 rounded-2xl flex flex-col items-center">
              <span className="text-[10px] font-bold text-blue-600 uppercase tracking-widest mb-1.5">
                Étape en cours
              </span>
              <span className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                <Loader2 className="h-3.5 w-3.5 animate-spin text-blue-500" />
                {visibleStepLabel ?? (erroredStep ? "Erreur" : "Démarrage…")}
              </span>
            </div>
          )}

          {/* Segmented Progress Indicator Bar */}
          <div className="flex h-[9px] w-full gap-[7px] mt-6">
            {Array.from({ length: segmentsTotal }).map((_, index) => {
              const stepStatus = steps[index]?.status;
              const isCompleted = stepStatus === "done" || index < doneCount;
              const isCurrent = stepStatus === "running" || (!stepStatus && index === activeIndex);
              const isError = stepStatus === "error";
              return (
                <span
                  key={index}
                  className={`h-[9px] flex-1 rounded-[30px] transition-all duration-300 ${
                    isError
                      ? "bg-[#FF3B30]"
                      : isCompleted
                        ? "bg-[#007AFF]"
                        : isCurrent
                          ? "animate-pulse bg-[#007AFF]"
                          : "bg-[#E2E8F0]"
                  }`}
                />
              );
            })}
          </div>

          <p className="text-xs text-slate-400 font-normal mt-5 leading-normal">
            {errorMessage ?? "Veuillez ne pas fermer cette fenêtre. Vos scénarios seront prêts dans quelques instants…"}
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}
